import { DIFF_TYPE } from '../../../../../../constnats/diff';
import { ReportField } from '../../../../../../constnats/reportConstants';
import type { ReportType } from '../../../../../../schemas/report.schemas';
import type { ReportDiffType, TableDiffType } from '../../../../../../schemas/schemaDiff.schemas';
import { getReportUniqueBusinessKey } from '../../../../../../utils/common';
import { ReportsUtils } from '../../../../../../utils/reportsUtils';
import { isPropDiffChanged } from './diffPredicates';
import { REPORT_FIELD_DIFF_PROPERTY } from './constants';
import type {
    PropertyDiffsByReportType,
    ReportsDiffTableData,
    ReportWithDiffType,
    ResolvedReportsDiffType,
    ResolvedTableDiffType,
} from './types';

export function resolveAffectedReportId(
    affectedReportId: string,
    fieldDiffs: ReportDiffType['fieldDiffs'],
): string {
    const versionDiff = fieldDiffs[ReportField.TYPE]?.[REPORT_FIELD_DIFF_PROPERTY.CORE_VALUE];
    const formatDiff = fieldDiffs[ReportField.REPORT_TYPE]?.[REPORT_FIELD_DIFF_PROPERTY.CORE_VALUE];
    const versionChange = versionDiff && isPropDiffChanged(versionDiff) ? versionDiff : undefined;
    const formatChange = formatDiff && isPropDiffChanged(formatDiff) ? formatDiff : undefined;

    if (!versionChange && !formatChange) {
        return affectedReportId;
    }

    const [year, period, format, version] = affectedReportId.split('/');
    return `${year}/${period}/${formatChange?.targetVal ?? format}/${versionChange?.targetVal ?? version}`;
}

export function resolveTableDiffReportIds(tableDiff: TableDiffType): ResolvedTableDiffType {
    return {
        ...tableDiff,
        reportsDiff: {
            ...tableDiff.reportsDiff,
            reports: tableDiff.reportsDiff.reports.map((reportDiff) => ({
                ...reportDiff,
                resolvedAffectedReportId: reportDiff.diffType === DIFF_TYPE.CHANGED
                    ? resolveAffectedReportId(reportDiff.affectedReportId, reportDiff.fieldDiffs)
                    : reportDiff.affectedReportId,
            })),
        },
    };
}

// Affected report IDs put format before version; frontend report keys do the reverse.
export function affectedReportIdToBusinessKey(affectedReportId: string): string {
    const [year, period, format, version] = affectedReportId.split('/');
    return `${year}/${period}/${version}/${format}`;
}

export function buildReportsDiffTableData(
    referenceReports: ReportType[],
    targetReports: ReportType[],
    reportsDiff: ResolvedReportsDiffType,
): ReportsDiffTableData {
    const differencesByReport: PropertyDiffsByReportType = {};
    const referenceReportsByDisplayedKey = new Map<string, ReportType>();
    const reports: ReportWithDiffType[] = [];

    for (const reportDiff of reportsDiff.reports) {
        const referenceKey = affectedReportIdToBusinessKey(reportDiff.affectedReportId);
        const resolvedKey = affectedReportIdToBusinessKey(reportDiff.resolvedAffectedReportId);
        const referenceReport = reportDiff.diffType === DIFF_TYPE.ADDED
            ? undefined
            : referenceReports.find((report) => getReportUniqueBusinessKey(report) === referenceKey);
        const matchingReport = reportDiff.diffType === DIFF_TYPE.REMOVED
            ? referenceReport
            : targetReports.find((report) => getReportUniqueBusinessKey(report) === resolvedKey);

        // A displayed report must exist in the corresponding branch.
        if (!matchingReport) {
            const affectedIdDetails = reportDiff.resolvedAffectedReportId !== reportDiff.affectedReportId
                ? ` (affected report: ${reportDiff.affectedReportId})`
                : '';
            throw new Error(`Report ${reportDiff.resolvedAffectedReportId} was not found${affectedIdDetails}`);
        }

        const displayedKey = getReportUniqueBusinessKey(matchingReport);
        reports.push({ ...matchingReport, diffType: reportDiff.diffType });
        if (referenceReport) {
            // The paired reference can have a different version or format from the displayed report.
            referenceReportsByDisplayedKey.set(displayedKey, referenceReport);
        }

        if (reportDiff.diffType === DIFF_TYPE.CHANGED) {
            for (const [descriptorId, properties] of Object.entries(reportDiff.fieldDiffs)) {
                for (const [property, diff] of Object.entries(properties)) {
                    if (isPropDiffChanged(diff)) {
                        differencesByReport[displayedKey] ??= {};
                        differencesByReport[displayedKey][descriptorId] ??= {};
                        differencesByReport[displayedKey][descriptorId][property] = diff;
                    }
                }
            }
        }
    }

    return {
        reports: reports.sort((a, b) => ReportsUtils.compare(a, b, true) ?? 0),
        differencesByReport,
        referenceReportsByDisplayedKey,
    };
}
